import React, { useEffect, useMemo, useState } from "react";
import {
  Grid,
  TextField,
  Typography,
  CircularProgress,
  Box,
  Card,
  Button,
  FormControlLabel,
  Checkbox,
  Autocomplete,
  InputAdornment,
  Paper,
} from "@mui/material";
import { FieldArray, FormikProvider, useFormik } from "formik";
import { useStyles } from "../../../theme/appStyles";
import { useNotifier } from "../../../Core/Notifier";
import {
  useAssignClassesCreateMutation,
  useGetsearchSatffQuery,
  useGetallLocationQuery,
  useGetallSubjectQuery,
  useGetStaffGradesAssignStaffGetPerentQuery,
  useGetTeamQuery,
} from "../../../Api/attoDeskApi";
import {
  CustomerOption,
  IClassCreate,
  IStudentLocation,
  ITeam,
  IGrade,
  ISubject,
  ISubjectGradePerent,
} from "../../../Api/Interface/api.interface";
import { ClassType, TeachMood, WeekDays } from "../../../Core/Enum/enum";
import useDebounce from "../../../Components/SearchDebonce/useDebounce";
import { IoSearchSharp } from "react-icons/io5";
import { styled, useTheme, Theme, CSSObject } from "@mui/material/styles";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import PremiumTimePicker from "../../../Components/PremiumTimePicker";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { resolveSafeReturnUrl } from "./assignmentContext";
import LoadingPage from "../../../Components/LoadingDialog/LoadingPage";
import BackDrop from "../../../Components/Loader/BackDrop";

const ItemList = styled(Paper, {
  shouldForwardProp: (prop) => prop !== "selected",
})<{ selected?: boolean }>(({ theme, selected }) => ({
  // display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  padding: theme.spacing(1, 2),
  borderRadius: 8,
  cursor: "pointer",
  transition: "all 0.2s ease",
  boxShadow: "none",

  backgroundColor: selected
    ? theme.palette.primary.main
    : theme.palette.background.paper,

  color: selected ? "#ffffff" : theme.palette.text.primary,

  "& .MuiTypography-root": {
    color: selected ? "#ffffff" : theme.palette.text.primary,
  },

  "&:hover": {
    // boxShadow: theme.shadows[3],
    backgroundColor: selected
      ? theme.palette.primary.dark
      : theme.palette.action.hover,
  },
}));

const studyMoodTecher = (unit: string) => {
  switch (unit) {
    case TeachMood.Online:
      return "Online";
    case TeachMood.Offline:
      return "InPerson";
    
    default:
      return "";
  }
};

const Index = () => {
  const { id: studentId } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const classes = useStyles();
  const theme = useTheme();
  const navigate = useNavigate();
  const { showErrorMessage, showMessage } = useNotifier();
  const [currentSearchQuery, setCurrentSearchQuery] = useState("");
  const debouncedSearchQuery = useDebounce(currentSearchQuery, 300);
  //   const [getStaffID, setGetStaffId] = useState("");
  const [getStaffID, setGetStaffId] = useState(String(studentId ?? ""));

  const [newCompany, { isLoading }] = useAssignClassesCreateMutation();
  const [selectedSubject, setSelectedSubject] = useState<number[]>([]);
  const [selectedWeekDays, setSelectedWeeekDays] = useState<string[]>([]);

  const { data: locationData, isLoading: locationIsLoading } =
    useGetallLocationQuery({
      searchText: "",
    });

  const {
    data: studentPerentData,
    isLoading: studentPerentIsLoading,
    refetch,
  } = useGetTeamQuery({
    searchText: currentSearchQuery,
    id: "",
  });

  const {
    data: subjectData,
    isLoading: subjectIsLoading,
    refetch: subjectRefetch,
  } = useGetallSubjectQuery({
    searchText: "",
  });

  const {
    data: subjectGradeData,
    isLoading: subjectGradeIsLoading,
    refetch: subjectGradeRefetch,
    isError,
  } = useGetStaffGradesAssignStaffGetPerentQuery({
    id: String(getStaffID),
  });

  const studentPerentList = useMemo(() => {
    return (studentPerentData?.data ?? []) as ITeam[];
  }, [studentPerentData?.data]);

  const locationList = useMemo(() => {
    return (locationData?.data ?? []) as IStudentLocation[];
  }, [locationData?.data]);

  const subjectDataList: ISubject[] = Array.isArray(subjectData?.data)
    ? (subjectData?.data as ISubject[])
    : [];

  const subjectGRadeDataList: IGrade[] = Array.isArray(subjectGradeData?.data)
    ? (subjectGradeData?.data as IGrade[])
    : [];

  useEffect(() => {
    if (debouncedSearchQuery.length >= 2 || debouncedSearchQuery === "") {
      setCurrentSearchQuery(debouncedSearchQuery);
      refetch();
    }
  }, [debouncedSearchQuery]);

  // useEffect(() => {
  //   if (data?.subject_ids) {
  //     setSelectedCategories(data.subject_ids);
  //   }
  // }, [data]);

  const formik = useFormik<IClassCreate>({
    initialValues: {
      staff_id: String(studentId),
      location_id: "",
      class_mode: "",
      class_type: "",
      number_of_student: null,
      select_grade_subject_ids: [],
      period_based_from_date: null,
      period_based_to_date: null,
      notes: "",
      selected_days: [],
      startTime: "",
      endTime: "",
    },
    enableReinitialize: true,

    validate: (values) => {
      const errors: { [key: string]: string } = {};
      const today = dayjs().startOf("day");

      if (values.period_based_from_date) {
        const fromDate = values.period_based_from_date.startOf("day");

        if (fromDate.isBefore(today)) {
          errors.period_based_from_date = "From date cannot be in the past";
        }
      }

      if (values.period_based_from_date && values.period_based_to_date) {
        const fromDate = values.period_based_from_date.startOf("day");
        const toDate = values.period_based_to_date.startOf("day");

        if (toDate.isBefore(fromDate)) {
          errors.period_based_to_date =
            "To date must be the same as or after From date";
        }
      }

      const isValidTime = (t: string) => /^(?:[01]\d|2[0-3]):[0-5]\d$/.test(t);

      const toMinutes = (time: string, spansNextDay: boolean = false) => {
        const [h, m] = time.split(":").map(Number);
        let total = h * 60 + m;
        return spansNextDay ? total + 24 * 60 : total;
      };

      if (!values.startTime || !isValidTime(values.startTime)) {
        errors.startTime = "Start time is required (HH:mm)";
      }

      if (!values.endTime || !isValidTime(values.endTime)) {
        errors.endTime = "End time is required (HH:mm)";
      }

      if (
        values.startTime &&
        values.endTime &&
        isValidTime(values.startTime) &&
        isValidTime(values.endTime)
      ) {
        // const start = dayjs(values.startTime, "HH:mm");
        // const end = dayjs(values.endTime, "HH:mm");

        const startMin = toMinutes(values.startTime);
        const endMin = toMinutes(values.endTime);

        // if (!end.isAfter(start)) {
        //   errors.endTime = "End time must be after Start time";
        // }

        if (endMin <= startMin) {
          errors.endTime = "End time must be after start time";
        }
      }

      return errors;
    },

    onSubmit: async (values, { resetForm }) => {
      try {
        const updatedData = {
          staff_id: values.staff_id,
          location_id: values.location_id,
          class_mode: values.class_mode,
          class_type: values.class_type,
          number_of_student: values.number_of_student,
          select_grade_subject_ids: selectedSubject,
          notes: values.notes,
          selected_days: selectedWeekDays,
          period_based_from_date: values.period_based_from_date
            ? values.period_based_from_date.format("YYYY-MM-DD")
            : null,
          period_based_to_date: values.period_based_to_date
            ? values.period_based_to_date.format("YYYY-MM-DD")
            : null,
          startTime: values.startTime,
          endTime: values.endTime,
        };

        const response = await newCompany(updatedData).unwrap();
        if (!response.status) {
          showErrorMessage(response.message);
        } else {
          showMessage("Class created successfully.");
          // Return to EXACTLY the page this was opened from (validated
          // `return_url`), defaulting to All Classes — NOT a hardcoded staff/
          // student profile. Entry points that want to land back on a profile
          // (e.g. Staff Profile) pass their own return_url. The new class id is
          // carried so the destination can highlight it; data refreshes via the
          // create mutation's cache invalidation.
          const back = resolveSafeReturnUrl(searchParams.get("return_url"));
          const newClassId = (response as any)?.data?.class_id;
          const sep = back.includes("?") ? "&" : "?";
          navigate(newClassId ? `${back}${sep}created_class_id=${newClassId}` : back);
          resetForm();
        }
      } catch (error) {
        showErrorMessage("Something went wrong");
      }
    },
  });

  const formValid = useMemo(() => {
    return formik.values.staff_id === "" ||
      formik.values.staff_id === undefined ||
      formik.values.startTime === "" ||
      formik.values.endTime === ""
      ? false
      : true;
  }, [formik]);

  const handleClassFromChange = (value: string) => {
    formik.setFieldValue("class_mode", value);
  };

  const handleClassTypeFromChange = (value: string) => {
    formik.setFieldValue("class_type", value);
  };

  const studentPerentOptions = useMemo(() => {
    if (!Array.isArray(studentPerentList)) return [];
    return studentPerentList.map((cust) => ({
      label: `${cust.teamFirstName || ""} ${cust.middle_name || ""} ${
        cust.teamLastName || ""
      }`,
      value: cust.id,
    }));
  }, [studentPerentList]);

  const locationOptions = useMemo(() => {
    if (!Array.isArray(locationList)) return [];
    return locationList.map((cust) => ({
      label: `${cust.location_name || cust.location_address}`,
      value: cust.id,
    }));
  }, [locationList]);

  const subjectMap = useMemo(() => {
    const map = new Map();
    subjectDataList?.forEach((user) => {
      map.set(user.id, `${user.subject_name}`);
    });
    return map;
  }, [subjectDataList]);

  const cancelFunction = () => {
    formik.resetForm();
  };

  const handleSubjectChange = (categoryId: number) => {
    setSelectedSubject((prev) =>
      prev.includes(categoryId)
        ? prev.filter((id) => id !== categoryId)
        : [...prev, categoryId]
    );
  };

  const handleWeekDaysChange = (weekday: string) => {
    setSelectedWeeekDays((prev) =>
      prev.includes(weekday)
        ? prev.filter((day) => day !== weekday)
        : [...prev, weekday]
    );
  };

  const pageIsLoading =
    locationIsLoading ||
    studentPerentIsLoading ||
    subjectIsLoading ||
    subjectGradeIsLoading;

  if (pageIsLoading) {
    return <LoadingPage open={pageIsLoading} />;
  }

  return (
    <Grid container spacing={3}>
      <Grid item lg={12} md={12} sm={12} xs={12}>
        <FormikProvider value={formik}>
          <form onSubmit={formik.handleSubmit}>
            <Grid container spacing={3}>
              <Grid item lg={12} md={12} sm={12} xs={12}>
                <Card className={classes.card}>
                  <Grid
                    container
                    justifyContent={"space-between"}
                    spacing={5}
                    sx={{ borderBottom: 1, padding: 2 }}
                    className={classes.titleGrid}
                  >
                    <Grid
                      item
                      lg={12}
                      md={12}
                      sm={12}
                      xs={12}
                      pt={2}
                      alignContent={"center"}
                    >
                      <Typography className={classes.cardTitle}>
                        Select Staff
                      </Typography>
                    </Grid>
                  </Grid>

                  <Grid container spacing={2} p="24px">
                    <Grid item lg={12} md={12} sm={12} xs={12}>
                      <Box
                        display="flex"
                        alignItems="center"
                        gap={2}
                        flexWrap={{ xs: "wrap", md: "nowrap" }}
                      >
                        <Box flex={1}>
                          <Autocomplete
                            disableClearable
                            popupIcon={null}
                            forcePopupIcon={false}
                            options={studentPerentOptions}
                            loading={studentPerentIsLoading}
                            getOptionLabel={(option: CustomerOption) =>
                              option.label
                            }
                            value={
                              studentPerentOptions.find(
                                (opt) =>
                                  opt.value === Number(formik.values.staff_id)
                              ) || undefined
                            }
                            onChange={(_, newValue: CustomerOption | null) => {
                              const staffId = newValue?.value
                                ? String(newValue.value)
                                : "";

                              formik.setFieldValue("staff_id", staffId);
                              setGetStaffId(staffId);
                              setSelectedSubject([]);
                              setSelectedWeeekDays([]);
                            }}
                            onInputChange={(_, inputValue, reason) => {
                              if (reason === "input") {
                                setCurrentSearchQuery(inputValue);
                              }
                            }}
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                placeholder="Search Staff"
                                size="small"
                                InputProps={{
                                  ...params.InputProps,
                                  endAdornment: (
                                    <InputAdornment position="end">
                                      {studentPerentIsLoading ? (
                                        <CircularProgress size={18} />
                                      ) : (
                                        <IoSearchSharp
                                          style={{ fontSize: "24px" }}
                                        />
                                      )}
                                    </InputAdornment>
                                  ),
                                }}
                              />
                            )}
                            sx={{ width: "100%" }}
                          />
                        </Box>
                      </Box>
                    </Grid>
                  </Grid>
                </Card>
              </Grid>
              <Grid item lg={4} md={6} sm={12} xs={12}>
                <Card className={classes.card} sx={{ height: "100%" }}>
                  <Grid
                    container
                    justifyContent={"space-between"}
                    spacing={5}
                    sx={{ borderBottom: 1, padding: 2 }}
                    className={classes.titleGrid}
                  >
                    <Grid
                      item
                      lg={12}
                      md={12}
                      sm={12}
                      xs={12}
                      pt={2}
                      alignContent={"center"}
                    >
                      <Typography className={classes.cardTitle}>
                        Number of Student
                      </Typography>
                    </Grid>
                  </Grid>

                  <Grid container spacing={2} p="24px">
                    <Grid item lg={12} md={12} sm={12} xs={12}>
                      <TextField
                        placeholder="Number of Student"
                        size="small"
                        {...formik.getFieldProps("number_of_student")}
                        type="number"
                        className={classes.textField}
                        InputProps={{
                          sx: {
                            fontSize: 14,
                          },
                        }}
                        InputLabelProps={{
                          sx: {
                            fontSize: 14,
                          },
                        }}
                      />
                    </Grid>
                  </Grid>
                </Card>
              </Grid>
              <Grid item lg={4} md={6} sm={12} xs={12}>
                <Card className={classes.card} sx={{ height: "100%" }}>
                  <Grid
                    container
                    justifyContent={"space-between"}
                    spacing={5}
                    sx={{ borderBottom: 1, padding: 2 }}
                    className={classes.titleGrid}
                  >
                    <Grid
                      item
                      lg={12}
                      md={12}
                      sm={12}
                      xs={12}
                      pt={2}
                      alignContent={"center"}
                    >
                      <Typography className={classes.cardTitle}>
                        Location
                      </Typography>
                    </Grid>
                  </Grid>

                  <Grid container spacing={2} p="24px">
                    <Grid item lg={12} md={12} sm={12} xs={12}>
                      <Autocomplete
                        options={locationOptions}
                        loading={locationIsLoading}
                        getOptionLabel={(option: CustomerOption) =>
                          option.label
                        }
                        value={
                          locationOptions.find(
                            (opt) =>
                              opt.value === Number(formik.values.location_id)
                          ) || null
                        }
                        onChange={(_, newValue: CustomerOption | null) => {
                          formik.setFieldValue(
                            "location_id",
                            newValue?.value || ""
                          );
                        }}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            placeholder="Location"
                            size="small"
                            InputLabelProps={{ shrink: true }}
                            className="textField"
                            InputProps={{
                              ...params.InputProps,
                              endAdornment: (
                                <>
                                  {locationIsLoading ? (
                                    <CircularProgress size={20} />
                                  ) : null}
                                  {params.InputProps.endAdornment}
                                </>
                              ),
                            }}
                          />
                        )}
                        sx={{ width: "100%" }}
                      />
                    </Grid>
                  </Grid>
                </Card>
              </Grid>

              <Grid item lg={4} md={6} sm={12} xs={12}>
                <Card className={classes.card} sx={{ height: "100%" }}>
                  <Grid
                    container
                    justifyContent={"space-between"}
                    spacing={5}
                    sx={{ borderBottom: 1, padding: 2 }}
                    className={classes.titleGrid}
                  >
                    <Grid
                      item
                      lg={12}
                      md={12}
                      sm={12}
                      xs={12}
                      pt={2}
                      alignContent={"center"}
                    >
                      <Typography className={classes.cardTitle}>
                        Select Class Mode
                      </Typography>
                    </Grid>
                  </Grid>

                  <Grid container spacing={2} p="24px">
                    {Object.values(TeachMood).map((type) => (
                      <Grid item key={type}>
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={formik.values.class_mode === type}
                              onChange={() => handleClassFromChange(type)}
                              size="small"
                            />
                          }
                          label={studyMoodTecher(type)}
                        />
                      </Grid>
                    ))}
                  </Grid>
                </Card>
              </Grid>
              <Grid item lg={4} md={6} sm={12} xs={12}>
                <Card className={classes.card} sx={{ height: "100%" }}>
                  <Grid
                    container
                    justifyContent={"space-between"}
                    spacing={5}
                    sx={{ borderBottom: 1, padding: 2 }}
                    className={classes.titleGrid}
                  >
                    <Grid
                      item
                      lg={12}
                      md={12}
                      sm={12}
                      xs={12}
                      pt={2}
                      alignContent={"center"}
                    >
                      <Typography className={classes.cardTitle}>
                        Class Type
                      </Typography>
                    </Grid>
                  </Grid>

                  <Grid container spacing={2} p="24px">
                    {Object.values(ClassType).map((type) => (
                      <Grid item key={type}>
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={formik.values.class_type === type}
                              onChange={() => handleClassTypeFromChange(type)}
                              size="small"
                            />
                          }
                          label={type}
                        />
                      </Grid>
                    ))}
                  </Grid>
                </Card>
              </Grid>
              {formik.values.class_type === ClassType["Regular"] && (
                <Grid item lg={4} md={6} sm={12} xs={12}>
                  <Card
                    className={classes.card}
                    sx={{
                      height: "100%",
                      display: "flex",
                      alignItems: "center",
                      p: 3,
                      bgcolor: "rgba(99,102,241,0.05)",
                      border: "1px dashed rgba(99,102,241,0.4)",
                    }}
                  >
                    <Typography sx={{ fontSize: 13, color: "text.secondary" }}>
                      Classes will be created for the remainder of this month and
                      the next 3 months. Occurrences will be generated through{" "}
                      <b>
                        {dayjs().add(3, "month").endOf("month").format("MMMM D, YYYY")}
                      </b>
                      . You don&apos;t need to set an end date for Regular classes.
                    </Typography>
                  </Card>
                </Grid>
              )}
              {formik.values.class_type === ClassType["Period-Based"] && (
                <>
                  <Grid item lg={4} md={6} sm={12} xs={12}>
                    <Card className={classes.card} sx={{ height: "100%" }}>
                      <Grid
                        container
                        justifyContent={"space-between"}
                        spacing={5}
                        sx={{ borderBottom: 1, padding: 2 }}
                        className={classes.titleGrid}
                      >
                        <Grid
                          item
                          lg={12}
                          md={12}
                          sm={12}
                          xs={12}
                          pt={2}
                          alignContent={"center"}
                        >
                          <Typography className={classes.cardTitle}>
                            Select Date
                          </Typography>
                        </Grid>
                      </Grid>

                      <Grid container spacing={2} p="24px">
                        <Grid item lg={6} md={12} sm={12} xs={12}>
                          <LocalizationProvider dateAdapter={AdapterDayjs}>
                            <DatePicker
                              value={formik.values.period_based_from_date}
                              onChange={(date) =>
                                formik.setFieldValue(
                                  "period_based_from_date",
                                  date
                                )
                              }
                              slotProps={{
                                textField: {
                                  placeholder: "From",
                                  size: "small",
                                  sx: { width: "100%" },
                                  error: Boolean(
                                    formik.errors.period_based_from_date
                                  ),
                                  helperText:
                                    formik.errors.period_based_from_date,
                                  InputProps: {
                                    sx: { fontSize: 14 },
                                  },
                                  InputLabelProps: {
                                    sx: { fontSize: 14 },
                                  },
                                },
                              }}
                            />
                          </LocalizationProvider>
                        </Grid>
                        <Grid item lg={6} md={12} sm={12} xs={12}>
                          <LocalizationProvider dateAdapter={AdapterDayjs}>
                            <DatePicker
                              value={formik.values.period_based_to_date}
                              onChange={(date) =>
                                formik.setFieldValue(
                                  "period_based_to_date",
                                  date
                                )
                              }
                              slotProps={{
                                textField: {
                                  placeholder: "To",
                                  size: "small",
                                  sx: { width: "100%" },
                                  error: Boolean(
                                    formik.errors.period_based_to_date
                                  ),
                                  helperText:
                                    formik.errors.period_based_to_date,
                                  InputProps: {
                                    sx: { fontSize: 14 },
                                  },
                                  InputLabelProps: {
                                    sx: { fontSize: 14 },
                                  },
                                },
                              }}
                            />
                          </LocalizationProvider>
                        </Grid>
                      </Grid>
                    </Card>
                  </Grid>

                  <Grid item lg={4} md={6} sm={12} xs={12}>
                    <Card className={classes.card} sx={{ height: "100%" }}>
                      <Grid
                        container
                        justifyContent={"space-between"}
                        spacing={5}
                        sx={{ borderBottom: 1, padding: 2 }}
                        className={classes.titleGrid}
                      >
                        <Grid
                          item
                          lg={12}
                          md={12}
                          sm={12}
                          xs={12}
                          pt={2}
                          alignContent={"center"}
                        >
                          <Typography className={classes.cardTitle}>
                            Memo
                          </Typography>
                        </Grid>
                      </Grid>

                      <Grid container spacing={2} p="24px">
                        <Grid item lg={12} md={12} sm={12} xs={12} pt={2}>
                          <TextField
                            placeholder="Meno .."
                            size="small"
                            multiline
                            rows={3}
                            {...formik.getFieldProps("notes")}
                            className={classes.discriptionTextField}
                            InputProps={{
                              sx: {
                                fontSize: 14,
                              },
                            }}
                            InputLabelProps={{
                              sx: {
                                fontSize: 14,
                              },
                            }}
                          />
                        </Grid>
                      </Grid>
                    </Card>
                  </Grid>
                </>
              )}

              <Grid item lg={12} md={12} sm={12} xs={12}>
                <Card
                  className={classes.card}
                  sx={{ width: "100%", padding: "24px" }}
                >
                  <Grid container spacing={1}>
                    {subjectIsLoading || subjectGradeIsLoading ? (
                      <Grid item lg={12} md={12} sm={12} xs={12}>
                        <Card
                          className={classes.card}
                          sx={{ height: "150px", p: "24px" }}
                        >
                          <Grid container>
                            <Grid item lg={12} md={12} sm={12} xs={12}>
                              <Box
                                display="flex"
                                justifyContent="center"
                                alignItems="center"
                                height="10vh"
                              >
                                <CircularProgress />
                                <Typography variant="subtitle1">
                                  Loading..
                                </Typography>
                              </Box>
                            </Grid>
                          </Grid>
                        </Card>
                      </Grid>
                    ) : isError || subjectGRadeDataList.length === 0 ? (
                      <Grid item lg={12} md={12} sm={12} xs={12}>
                        <Card
                          className={classes.card}
                          sx={{ height: "150px", p: "24px" }}
                        >
                          <Grid container>
                            <Grid item lg={12} md={12} sm={12} xs={12}>
                              <Typography textAlign={"center"}>
                                {isError
                                  ? "Error fetching data"
                                  : "Grades and subjects have not been assigned to the selected teacher"}
                              </Typography>
                            </Grid>
                          </Grid>
                        </Card>
                      </Grid>
                    ) : (
                      subjectGRadeDataList.map((item: IGrade) => {
                        return (
                          <Grid item lg={12} md={12} sm={12} xs={12}>
                            <Card
                              className={classes.card}
                              sx={{ width: "100%" }}
                              key={item.id}
                            >
                              <Grid container>
                                <Grid item lg={2} md={3} sm={4} xs={4}>
                                  <Card
                                    sx={{
                                      // backgroundColor: "#54b8e8",
                                      height: "100%",
                                      p: 1,
                                      alignItems: "center",
                                      justifyContent: "center",
                                      alignContent: "center",
                                      textAlign: "center",
                                    }}
                                  >
                                    <ItemList
                                      selected={true}
                                      sx={{
                                        backgroundColor: "#54b8e8",
                                        color: "#ffffff",
                                        textAlign: "center",
                                        alignItems: "center",
                                      }}
                                    >
                                      <Typography
                                        sx={{
                                          color: "#fff",
                                          fontWeight: 600,
                                          fontSize: "15px",
                                          textAlign: "center",
                                          alignItems: "center",
                                        }}
                                      >
                                        {item.grade}
                                      </Typography>
                                    </ItemList>
                                  </Card>
                                </Grid>
                                <Grid item lg={9} md={8} sm={8} xs={8}>
                                  <Card
                                    sx={{
                                      // backgroundColor: "#effaff",
                                      height: "100%",
                                      p: 1,
                                      alignItems: "center",
                                      justifyContent: "center",
                                      boxShadow: "none",
                                    }}
                                  >
                                    <Grid container spacing={1}>
                                      {item.selected_sub_array?.map(
                                        (
                                          item: ISubjectGradePerent,
                                          index: number
                                        ) => {
                                          const isSelected =
                                            selectedSubject.includes(item.id);

                                          return (
                                            <Grid item key={index}>
                                              <ItemList
                                                selected={isSelected}
                                                onClick={() =>
                                                  handleSubjectChange(item.id)
                                                }
                                                sx={{
                                                  background: isSelected
                                                    ? theme.palette.primary.main
                                                    : "#eef2f7",

                                                  color: isSelected
                                                    ? "#ffffff"
                                                    : theme.palette.primary
                                                        .main,
                                                }}
                                              >
                                                <Typography variant="body2">
                                                  {subjectMap.get(
                                                    item.subject_id
                                                  )}
                                                </Typography>
                                              </ItemList>
                                            </Grid>
                                          );
                                        }
                                      )}
                                    </Grid>
                                  </Card>
                                </Grid>
                              </Grid>
                            </Card>
                          </Grid>
                        );
                      })
                    )}
                  </Grid>
                </Card>
              </Grid>
              <Grid item lg={12} md={12} sm={12} xs={12}>
                <Card className={classes.card}>
                  <Grid container spacing={2} p="24px">
                    <Grid item>
                      <Grid container spacing={1}>
                        <Grid item lg={12} md={12} sm={12} xs={12}>
                          <Typography variant="subtitle1">Days</Typography>
                        </Grid>
                        {Object.values(WeekDays).map((day) => {
                          const isSelected = selectedWeekDays.includes(day);

                          return (
                            <Grid item key={day}>
                              <ItemList
                                selected={isSelected}
                                onClick={() => handleWeekDaysChange(day)}
                                sx={{
                                  background: isSelected
                                    ? theme.palette.primary.main
                                    : "#eef2f7",
                                  color: isSelected
                                    ? "#ffffff"
                                    : theme.palette.primary.main,
                                }}
                              >
                                <Typography variant="body2">{day}</Typography>
                              </ItemList>
                            </Grid>
                          );
                        })}
                      </Grid>
                    </Grid>
                    <Grid item>
                      <Grid container spacing={1}>
                        <Grid item>
                          <Grid container alignItems="center">
                            <Grid item lg={12} md={12} sm={12} xs={12}>
                              <Typography variant="subtitle1">
                                Start Time{" "}
                                <span className={classes.requiedfield}>*</span>
                              </Typography>
                            </Grid>
                            <Grid item lg={12} md={12} sm={12} xs={12}>
                              <PremiumTimePicker
                                label=""
                                value={formik.values.startTime}
                                clearable={false}
                                onChange={(time) => formik.setFieldValue(`startTime`, time)}
                              />
                              {formik.touched.startTime &&
                                formik.errors.startTime && (
                                  <Typography color="error" fontSize={13}>
                                    {formik.errors.startTime}
                                  </Typography>
                                )}
                            </Grid>
                          </Grid>
                        </Grid>
                        <Grid item>
                          <Grid container alignItems="center">
                            <Grid item lg={12} md={12} sm={12} xs={12}>
                              <Typography variant="subtitle1">
                                End Time{" "}
                                <span className={classes.requiedfield}>*</span>
                              </Typography>
                            </Grid>
                            <Grid item lg={12} md={12} sm={12} xs={12}>
                              <PremiumTimePicker
                                label=""
                                value={formik.values.endTime}
                                clearable={false}
                                onChange={(time) => formik.setFieldValue(`endTime`, time)}
                              />
                              {formik.touched.endTime &&
                                formik.errors.endTime && (
                                  <Typography color="error" fontSize={13}>
                                    {formik.errors.endTime}
                                  </Typography>
                                )}
                            </Grid>
                          </Grid>
                        </Grid>
                      </Grid>
                    </Grid>
                  </Grid>
                </Card>
              </Grid>
              <Grid item lg={12} md={12} sm={12} xs={12}>
                <Grid container spacing={1}>
                  <Grid item>
                    <Button
                      variant="contained"
                      className={classes.addButton}
                      onClick={() => formik.handleSubmit()}
                      disabled={!formValid || isLoading}
                    >
                      {isLoading && (
                        <CircularProgress
                          size={18}
                          className={classes.isLoading}
                          sx={{ marginRight: 1 }}
                        />
                      )}
                      Create
                    </Button>
                  </Grid>
                  <Grid item>
                    <Button
                      variant="outlined"
                      className={classes.addButton}
                      onClick={cancelFunction}
                    >
                      Reset
                    </Button>
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
          </form>
        </FormikProvider>
        {isLoading && <BackDrop open={isLoading} />}
      </Grid>
    </Grid>
  );
};

export default Index;
